// 模組07 訊息功能 — DcMessage mock 種子（notify-api 票 03）。
//
// store 直接存 `DcMessageDto`（snake_case，鍵集合＝swagger 9/29 版），回傳時不再投影 ——
// 「mock 跟 API 一模一樣」:mock 讀回的形狀就是真後端的形狀(9/30 SIT smoke 實測鍵集合與 swagger 一致)。
//
// 內容沿用舊 feed 種子(`notifications.js`,對稿 DCK-07「訊息樣式」寫的那批文案),
// 換成後端欄位:source→module_name、message→content、channel→channels_text、overdue→tag「到期」、
// actionLabel→action_text＋action_url(前端路由)。再補上舊種子沒有、但真後端會出現的情況:
//   - 一則 `message_category` 是**整數 0**、module_name／tag／action_* 全空(SIT 現況,9/30 實測)
//   - 幾則給**別人**的訊息(驗 targetCustomerId 真的有篩)
//   - 沒有前往連結的訊息(批次匯入完成通知;稿 2375:36350 也沒有連結)
import { notifications } from "./notifications";
import { NOTIFY_CATEGORY_BY_TAB } from "../../constants/notifyCategories";
import { MESSAGE_CATEGORIES } from "../../helpers/messageContract";

/** 主要的登入者(mock 帳號 id 1 = Overseer);其餘帳號各配幾則,換角色登入也看得到自己的訊息 */
export const MESSAGE_OWNER_ID = 1;
const OTHER_CUSTOMERS = [2, 3, 4, 5, 34];

// 舊 feed 的中文分類 → 後端 enum:經分頁 key 串起來,不另寫第三份對照表
// (中文↔分頁 key 在 constants/notifyCategories,分頁 key↔enum 在 helpers/messageContract)
const CATEGORY_ENUM = Object.fromEntries(MESSAGE_CATEGORIES.map((c) => [NOTIFY_CATEGORY_BY_TAB[c.tab], c.enumName]));
const CATEGORY_TEXT = Object.fromEntries(Object.entries(CATEGORY_ENUM).map(([zh, en]) => [en, zh]));

// 前往連結 → 前端路由(後端給的 action_url;舊 feed 是前端自己查表,現在由資料帶)
const ACTION_URL = {
  前往審核: "/dck/approvals",
  前往資產: "/dck/assets/hardware",
  // 報表模組還沒開放(devOnly 路由);CVE 報表的內容在硬體頁看得到 → 先帶到硬體頁
  前往報表: "/dck/assets/hardware",
  前往合約: "/dck/registry/contracts",
  前往工作項目: "/dck/accounts/work",
};

/** 一則 DcMessageDto 的完整鍵集合(swagger DcMessageDto,9/29) */
export function messageDto(fields) {
  const at = fields.sent_on || new Date().toISOString();
  return {
    title: null,
    content: "",
    category: null,
    message_category: "DataIncomplete",
    message_category_text: null,
    module_name: null,
    tag: null,
    action_url: null,
    action_text: null,
    notification_rule_id: 0,
    channels_text: "站內 通知",
    target_customer_id: MESSAGE_OWNER_ID,
    channels: "InApp",
    status: "Active",
    scheduled_on: null,
    sent_on: at,
    is_read: false,
    related_entity_type_id: 0,
    related_entity_id: 0,
    related_request_no: null,
    escalation_level: 1,
    tier2_customer_id: 0,
    tier3_customer_id: 0,
    next_escalation_on: null,
    is_resolved: false,
    created_on: at,
    updated_on: at,
    id: 0,
    ...fields,
  };
}

const fromFeed = (n, id, targetCustomerId) => {
  const messageCategory = CATEGORY_ENUM[n.category];
  const withEmail = (n.channel || "").includes("Email");
  // 批次匯入完成通知不帶連結(稿 2375:36350):驗「缺 action_url 不顯示連結」
  const noLink = n.source === "批次匯入";
  return messageDto({
    id,
    title: null,
    // 單據編號在稿上就是內文的一部分(「{單據編號}｜{發動者} 發動…」),後端組好再給
    content: `${n.docNo ? `${n.docNo}｜` : ""}${n.message}`,
    category: n.type || null,
    message_category: messageCategory,
    message_category_text: CATEGORY_TEXT[messageCategory] || null,
    module_name: n.source,
    tag: n.overdue ? "到期" : null,
    action_url: noLink ? null : ACTION_URL[n.actionLabel] || null,
    action_text: noLink ? null : n.actionLabel || null,
    channels_text: withEmail ? "站內＆Email 通知" : "站內 通知",
    channels: withEmail ? "InApp, Email" : "InApp",
    target_customer_id: targetCustomerId,
    sent_on: n.createdAt,
    created_on: n.createdAt,
    updated_on: n.createdAt,
    is_read: !!n.read,
    related_request_no: n.docNo || null,
  });
};

export function buildDcMessages() {
  const mine = notifications.map((n, i) => fromFeed(n, i + 1, MESSAGE_OWNER_ID));
  let id = mine.length;
  // 其他帳號:每人三則(取前三則樣板)—— targetCustomerId 篩錯的話登入者會看到這些
  const others = OTHER_CUSTOMERS.flatMap((cid) =>
    notifications.slice(0, 3).map((n) => {
      id += 1;
      return fromFeed({ ...n, message: `${n.message}（給帳號 ${cid}）` }, id, cid);
    })
  );
  // SIT 現況(9/30 實測):爬蟲告警沒設種類 → message_category 整數 0、module_name／tag／action_* 空、
  // category 是中文「資安漏洞」。時間放最舊,免得它排在第一頁的前三筆。
  id += 1;
  const oldest = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString();
  const uncategorized = messageDto({
    id,
    content: "資安漏洞告警：OpenSSL 3.0.x 有新公告 CVE-2026-1234（CVSS 9.8），請確認受影響資產。",
    category: "資安漏洞",
    message_category: 0,
    message_category_text: "",
    module_name: "",
    tag: "",
    action_url: "",
    action_text: "",
    channels_text: "站內＆小鈴噹 通知",
    channels: "InApp, Bell",
    target_customer_id: MESSAGE_OWNER_ID,
    sent_on: oldest,
    created_on: oldest,
    updated_on: oldest,
    escalation_level: 0,
  });
  return [...mine, ...others, uncategorized];
}
